/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useEffect, useMemo, useState } from "react";
import { CosmosTable as Table, CosmosTabs as Tabs, CosmosText, ScrollContainer } from "@components/elements";
import { EventsTableRowFragment, EventsTableSectionFragment } from "@cosmos/hygraph-schema";
import { css } from "@panda/css";
import { VStack } from "@panda/jsx";
import { safeGetTime } from "@components/utils";
import { EventsTableRow } from "./EventsTableRow.molecule";

export const EventsTable = ({
	upcomingTabLabel,
	pastTabLabel,
	titleColumnLabel,
	locationColumnLabel,
	detailsColumnLabel,
	events = [],
}: EventsTableSectionFragment): React.JSX.Element => {
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), 60000); // update every minute
		return () => clearInterval(interval);
	}, []);

	const { pastEvents, upcomingEvents } = useMemo(() => {
		const pastEvents: EventsTableRowFragment[] = [];
		const upcomingEvents: EventsTableRowFragment[] = [];

		events.forEach((event) => {
			const eventTime = safeGetTime(event.timeAndDate);
			if (eventTime < now) {
				pastEvents.push(event);
			} else {
				upcomingEvents.push(event);
			}
		});

		return {
			pastEvents: pastEvents.sort((a, b) => safeGetTime(b.timeAndDate) - safeGetTime(a.timeAndDate)), // DESC
			upcomingEvents: upcomingEvents.sort((a, b) => safeGetTime(a.timeAndDate) - safeGetTime(b.timeAndDate)), // ASC
		};
	}, [events, now]);

	// Determine default tab based on which has events
	const defaultIndex = useMemo(() => {
		return upcomingEvents.length > 0 ? 0 : 1;
	}, [upcomingEvents.length]);

	// Tab configuration for mapping
	const tabs = [
		{
			label: upcomingTabLabel || "Upcoming",
			events: upcomingEvents,
			fullLabel: `${upcomingTabLabel || "Upcoming"} (${upcomingEvents.length})`,
		},
		{
			label: pastTabLabel || "Past events",
			events: pastEvents,
			fullLabel: `${pastTabLabel || "Past events"} (${pastEvents.length})`,
		},
	];

	if (!events.some((event: EventsTableRowFragment) => !!event.title || !!event.location)) return <></>;

	return (
		<VStack gap="eds.dimension.32" alignItems="stretch">
			<Tabs defaultIndex={defaultIndex} size="lg" inactiveVariant="outline">
				<VStack gap="eds.dimension.32" alignItems="stretch">
					<Tabs.ButtonList>
						{tabs.map((tab, index) => (
							<Tabs.Button
								key={index}
								index={index}
								label={tab.label}
								count={tab.events.length}
								disabled={tab.events.length === 0}
								className={css({ fontWeight: "bold" })}
								analytics={{
									eventCategory: "button",
									interactionType: "click",
									eventAction: "select",
									eventLabel: tab.fullLabel,
									eventValue: {
										component: "EventsTable",
										section: "section",
									},
								}}
							/>
						))}
					</Tabs.ButtonList>

					{tabs.map((tab, index) => (
						<Tabs.Panel key={index} index={index}>
							<ScrollContainer>
								<Table minWidth="879px" gridTemplateColumns="9fr 5fr 20fr">
									<Table.Header>
										<Table.Column id="title">
											<CosmosText variant="uiMD" color="eds.text.secondary" text={titleColumnLabel || "Title"} />
										</Table.Column>
										<Table.Column id="location">
											<CosmosText variant="uiMD" color="eds.text.secondary" text={locationColumnLabel || "Location"} />
										</Table.Column>
										<Table.Column id="details">
											<CosmosText variant="uiMD" color="eds.text.secondary" text={detailsColumnLabel || "Detail"} />
										</Table.Column>
									</Table.Header>
									<Table.Body>
										{tab.events.map((event: EventsTableRowFragment) => {
											if (!event.title) return null;
											return <EventsTableRow key={event.id} {...event} />;
										})}
									</Table.Body>
								</Table>
							</ScrollContainer>
						</Tabs.Panel>
					))}
				</VStack>
			</Tabs>
		</VStack>
	);
};
